import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, Flame, Clock, ChevronRight, AlertTriangle, Heart, Sparkles, Shield } from "lucide-react";
import CourseEnrollmentModal from "../components/CourseEnrollmentModal";

const courseIcons = {
  tratak: Flame,
  chakra: Heart,
  tridevi: Sparkles,
  ganesh: Shield
};

const courseColors = {
  tratak: { bg: "from-sacred-gold/20 to-sacred-gold/5", border: "border-sacred-gold/30", text: "text-sacred-gold" },
  chakra: { bg: "from-sacred-purple/20 to-sacred-purple/5", border: "border-sacred-purple/30", text: "text-sacred-purple" },
  tridevi: { bg: "from-sacred-blue/20 to-sacred-blue/5", border: "border-sacred-blue/30", text: "text-sacred-blue" },
  ganesh: { bg: "from-sacred-green/20 to-sacred-green/5", border: "border-sacred-green/30", text: "text-sacred-green" }
};

export default function MayaManthanCourses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0B0F1A] to-[#1A1F3A] flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-2 border-sacred-gold/30 border-t-sacred-gold rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sacred-text-secondary font-body">Loading sacred programs...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F1A] to-[#1A1F3A] relative" data-testid="maya-courses">
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-sacred-purple/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-sacred-gold/5 rounded-full blur-3xl" />
      </div>

      {/* Back Button */}
      <button
        onClick={() => navigate('/maya-manthan')}
        className="fixed top-6 left-6 z-50 p-3 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-all duration-300"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-5 h-5 text-sacred-text-secondary" />
      </button>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sacred-card-glass backdrop-blur-xl border border-white/10 text-sacred-gold text-sm font-body mb-6">
            <Sparkles className="w-4 h-4" />
            Sacred Transformation Programs
          </span>
          <h1 className="font-heading text-4xl md:text-5xl text-sacred-text-primary mb-4">
            Begin Your Inner Journey
          </h1>
          <p className="text-sacred-text-secondary font-body max-w-2xl mx-auto leading-relaxed">
            Deep immersion programs for profound spiritual transformation. Each course is designed 
            to address specific aspects of your inner awakening.
          </p>
        </motion.div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {courses.map((course, index) => {
            const IconComponent = courseIcons[course.id] || Flame;
            const colors = courseColors[course.id] || courseColors.tratak;
            
            return (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`sacred-card p-8 transition-all duration-300 ${
                  course.primary_recommendation 
                    ? 'ring-2 ring-sacred-gold/30 border-sacred-gold/30' 
                    : course.recommended 
                    ? 'border-sacred-purple/20' 
                    : ''
                }`}
                data-testid={`course-card-${course.id}`}
              >
                {course.primary_recommendation && (
                  <div className="bg-sacred-gold/20 text-sacred-gold text-xs font-body px-4 py-1.5 rounded-full inline-block mb-4">
                    Recommended for Your Journey
                  </div>
                )}

                <div className="flex items-start gap-5 mb-5">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                    <IconComponent className={`w-7 h-7 ${colors.text}`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-sacred-text-primary mb-1">{course.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-sacred-text-muted font-body">
                      <Clock className="w-4 h-4" />
                      {course.duration}
                    </div>
                  </div>
                </div>

                <p className="text-sacred-text-secondary text-sm font-body mb-5 leading-relaxed">
                  {course.description}
                </p>

                {/* Important Note for Tratak */}
                {course.id === 'tratak' && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-5">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-amber-400 text-sm font-body">
                        <strong>Sacred Practice Note:</strong> Use only Diya with ghee or mustard oil. 
                        Do NOT use candles as they are considered Tamasik.
                      </p>
                    </div>
                  </div>
                )}

                {/* Benefits */}
                <div className="mb-6">
                  <p className="text-sacred-text-muted text-xs font-body uppercase tracking-wide mb-3">What You'll Experience</p>
                  <div className="flex flex-wrap gap-2">
                    {course.benefits?.slice(0, 3).map((benefit, i) => (
                      <span 
                        key={i}
                        className="bg-white/5 text-sacred-text-secondary text-xs font-body px-3 py-1.5 rounded-full"
                      >
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Price & CTA */}
                <div className="flex items-center justify-between pt-5 border-t border-white/5">
                  <div>
                    <span className="text-3xl font-heading text-sacred-gold">₹{course.price.toLocaleString()}</span>
                    <span className="text-sacred-text-muted text-sm font-body">/month</span>
                  </div>
                  <Button
                    onClick={() => setSelectedCourseId(course.id)}
                    className={`bg-gradient-to-r ${colors.bg} ${colors.text} border ${colors.border} font-body font-medium px-6 py-3 rounded-full hover:shadow-lg transition-all duration-300`}
                    data-testid={`enroll-btn-${course.id}`}
                  >
                    <Flame className="w-4 h-4 mr-2" /> Begin My Practice
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16"
        >
          <div className="bg-[#12182B]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-8 max-w-lg mx-auto">
            <h3 className="font-heading text-xl text-sacred-text-primary mb-3">
              Not Sure Where to Begin?
            </h3>
            <p className="text-sacred-text-secondary font-body text-sm mb-6">
              Speak with your guide to discover the perfect practice for your unique journey.
            </p>
            <Button
              onClick={() => navigate('/maya-manthan/guide')}
              className="bg-white/5 hover:bg-white/10 text-sacred-text-primary border border-white/10 font-body px-6 py-3 rounded-full"
            >
              Connect with Your Guide <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Enrollment Modal */}
      <CourseEnrollmentModal
        isOpen={!!selectedCourseId}
        onClose={() => setSelectedCourseId(null)}
        courseId={selectedCourseId}
        onNavigate={(path) => navigate(path)}
      />
    </div>
  );
}
